/* eslint-disable max-lines */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import ReactDatetime from 'react-datetime';

import { selectAllOnFocus } from '../../../../helpers/selectOnFocus';
// ============================================================
// Design tokens (from the handoff — Linear/Airtable chip style)
// ============================================================
export const FILTER_COLORS = {
  ink: '#2b3142',
  inkSoft: '#5a6271',
  inkMute: '#8b94a3',
  line: '#e9ecef',
  lineSoft: '#f1f3f5',
  bgSoft: '#f8f9fa',
  purple: '#6f57ff',
  purpleSoft: '#eeebff',
  red: '#e85d75',
};
const C = FILTER_COLORS;

// ============================================================
// Public types
// ============================================================
export type FilterFieldType = 'enum' | 'daterange' | 'boolean' | 'text' | 'number';

export type NumberOp = 'eq' | 'gte' | 'lte' | 'gt';

export interface FilterFieldDef {
  key: string;
  label: string;
  type: FilterFieldType;
  icon: string; // FontAwesome class fragment, e.g. 'fa-circle'
  /** Field path sent to the backend (e.g. 'status', 'title.name'). UI-agnostic. */
  backendField?: string;
}

export interface EnumOption {
  value: string;
  label: string;
}

export interface NumberFilterValue {
  op: NumberOp;
  value: number;
}

export type FilterValue = string[] | { from: string; to: string } | boolean | string | NumberFilterValue;

export interface ActiveFilter {
  key: string;
  value: FilterValue;
}

const NUMBER_OPS: { op: NumberOp; label: string; symbol: string }[] = [
  { op: 'eq', label: 'Equal to', symbol: '=' },
  { op: 'gte', label: 'Greater or equal', symbol: '≥' },
  { op: 'lte', label: 'Less or equal', symbol: '≤' },
  { op: 'gt', label: 'Greater than', symbol: '>' },
];

export function numberOpSymbol(op: NumberOp): string {
  return NUMBER_OPS.find((o) => o.op === op)?.symbol || '=';
}

export function matchesNumberFilter(cell: number, filter: NumberFilterValue): boolean {
  const c = Number(cell) || 0;
  const { op, value } = filter;
  if (op === 'eq') return c === value;
  if (op === 'gte') return c >= value;
  if (op === 'lte') return c <= value;
  return c > value;
}

// ============================================================
// Outside-click + Esc hook
// ============================================================
function useOutside(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return ref;
}

const popoverBaseStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  background: '#fff',
  border: `1px solid ${C.line}`,
  borderRadius: 10,
  boxShadow: '0 12px 32px rgba(20,30,50,.14), 0 2px 8px rgba(20,30,50,.06)',
  zIndex: 1000,
};

const footerBtnGhost: React.CSSProperties = {
  flex: 1,
  padding: '7px 0',
  border: `1px solid ${C.line}`,
  borderRadius: 7,
  background: '#fff',
  fontSize: 12.5,
  fontWeight: 700,
  color: C.inkSoft,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
const footerBtnPrimary: React.CSSProperties = {
  flex: 1,
  padding: '7px 0',
  border: 'none',
  borderRadius: 7,
  background: C.purple,
  fontSize: 12.5,
  fontWeight: 700,
  color: '#fff',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ============================================================
// Date helpers (value stored as YYYY-MM-DD)
// ============================================================
function fmtDate(d: string) {
  if (!d) return '…';
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${m}/${day}/${y}`;
}

// ============================================================
// Enum popover — multi-select with search + checkboxes
// ============================================================
function EnumPopover({
  field,
  options,
  selected,
  onApply,
  onClose,
}: {
  field: FilterFieldDef;
  options: EnumOption[];
  selected: string[];
  onApply: (vals: string[]) => void;
  onClose: () => void;
}) {
  const [local, setLocal] = useState<Set<string>>(new Set(selected));
  const [q, setQ] = useState('');
  const ref = useOutside(true, onClose);
  const filtered = useMemo(() => options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())), [options, q]);

  const toggle = (v: string) => {
    const n = new Set(local);
    n.has(v) ? n.delete(v) : n.add(v);
    setLocal(n);
  };

  return (
    <div ref={ref} style={{ ...popoverBaseStyle, width: 240, overflow: 'hidden' }}>
      <div style={{ padding: 8, borderBottom: `1px solid ${C.lineSoft}` }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: C.bgSoft,
            borderRadius: 7,
            padding: '6px 9px',
          }}
        >
          <i className="fas fa-search" style={{ color: C.inkMute, fontSize: 12 }} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Filter ${field.label.toLowerCase()}…`}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13,
              color: C.ink,
              width: '100%',
              fontFamily: 'inherit',
            }}
          />
        </div>
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4 }}>
        {filtered.length === 0 && (
          <div style={{ padding: '14px 10px', fontSize: 12.5, color: C.inkMute, textAlign: 'center' }}>No options</div>
        )}
        {filtered.map((o) => {
          const on = local.has(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => toggle(o.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                textAlign: 'left',
                padding: '7px 8px',
                border: 'none',
                background: 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 13,
                color: C.ink,
                fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = C.lineSoft)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <span
                style={{
                  width: 17,
                  height: 17,
                  borderRadius: 4,
                  flexShrink: 0,
                  border: `1.5px solid ${on ? C.purple : '#cbd2dc'}`,
                  background: on ? C.purple : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  transition: 'all .12s',
                }}
              >
                {on && <i className="fas fa-check" style={{ fontSize: 10 }} />}
              </span>
              <span style={{ flex: 1 }}>{o.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 8, borderTop: `1px solid ${C.lineSoft}` }}>
        <button type="button" onClick={() => setLocal(new Set())} style={footerBtnGhost}>
          Clear
        </button>
        <button
          type="button"
          onClick={() => {
            onApply(Array.from(local));
            onClose();
          }}
          style={footerBtnPrimary}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Text popover — single free-text value (fallback for enums
// whose option list we don't have a source for yet)
// ============================================================
function TextPopover({
  field,
  value,
  onApply,
  onClose,
}: {
  field: FilterFieldDef;
  value: string;
  onApply: (v: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(value || '');
  const ref = useOutside(true, onClose);
  const apply = () => {
    onApply(text.trim());
    onClose();
  };
  return (
    <div ref={ref} style={{ ...popoverBaseStyle, width: 240, padding: 12 }}>
      <label
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.inkMute,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {field.label}
      </label>
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') apply();
        }}
        placeholder={`Type ${field.label.toLowerCase()}…`}
        style={{
          width: '100%',
          marginTop: 6,
          padding: '8px 10px',
          border: `1px solid ${C.line}`,
          borderRadius: 7,
          fontSize: 13,
          color: C.ink,
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={() => setText('')} style={footerBtnGhost}>
          Clear
        </button>
        <button type="button" onClick={apply} style={footerBtnPrimary}>
          Apply
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Number popover — operator (=, ≥, ≤, >) + numeric value
// ============================================================
function NumberPopover({
  field,
  value,
  onApply,
  onClose,
}: {
  field: FilterFieldDef;
  value: NumberFilterValue | null;
  onApply: (v: NumberFilterValue) => void;
  onClose: () => void;
}) {
  const [op, setOp] = useState<NumberOp>(value?.op || 'gte');
  const [text, setText] = useState(value && !Number.isNaN(value.value) ? String(value.value) : '');
  const ref = useOutside(true, onClose);
  const apply = () => {
    const num = text.trim() === '' ? NaN : Number(text);
    onApply({ op, value: num });
    onClose();
  };
  const controlStyle: React.CSSProperties = {
    padding: '8px 9px',
    border: `1px solid ${C.line}`,
    borderRadius: 7,
    fontSize: 13,
    color: C.ink,
    fontFamily: 'inherit',
    background: '#fff',
    outline: 'none',
  };
  return (
    <div ref={ref} style={{ ...popoverBaseStyle, width: 250, padding: 12 }}>
      <label
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.inkMute,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
        }}
      >
        {field.label}
      </label>
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <select
          value={op}
          onChange={(e) => setOp(e.target.value as NumberOp)}
          style={{ ...controlStyle, flex: '0 0 118px', cursor: 'pointer' }}
        >
          {NUMBER_OPS.map((o) => (
            <option key={o.op} value={o.op}>
              {`${o.symbol}  ${o.label}`}
            </option>
          ))}
        </select>
        <input
          autoFocus
          type="number"
          {...selectAllOnFocus}
          min={0}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') apply();
          }}
          placeholder="0"
          style={{ ...controlStyle, flex: 1, width: '100%' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" onClick={() => setText('')} style={footerBtnGhost}>
          Clear
        </button>
        <button type="button" onClick={apply} style={footerBtnPrimary}>
          Apply
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Date-range popover
// ============================================================
function DatePopover({
  value,
  onApply,
  onClose,
}: {
  value: { from: string; to: string } | null;
  onApply: (v: { from: string; to: string }) => void;
  onClose: () => void;
}) {
  const [from, setFrom] = useState(value?.from || '');
  const [to, setTo] = useState(value?.to || '');
  const ref = useOutside(true, onClose);
  const dateInput: React.CSSProperties = {
    width: '100%',
    marginTop: 4,
    padding: '7px 9px',
    border: `1px solid ${C.line}`,
    borderRadius: 7,
    fontSize: 13,
    color: C.ink,
    fontFamily: 'inherit',
    outline: 'none',
  };
  const toStored = (val: any) => {
    const parsed = moment.isMoment(val) ? val : moment(val, 'MM/DD/YYYY', true);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  };
  const toDisplay = (stored: string) => (stored ? moment(stored, 'YYYY-MM-DD') : '');
  return (
    <div ref={ref} style={{ ...popoverBaseStyle, width: 230, padding: 12 }}>
      <label
        style={{ fontSize: 11, fontWeight: 700, color: C.inkMute, textTransform: 'uppercase', letterSpacing: '.05em' }}
      >
        From
      </label>
      <ReactDatetime
        value={toDisplay(from)}
        onChange={(val) => setFrom(toStored(val))}
        dateFormat="MM/DD/YYYY"
        timeFormat={false}
        closeOnSelect
        inputProps={{ placeholder: 'MM/DD/YYYY', style: dateInput }}
      />
      <label
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.inkMute,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          display: 'block',
          marginTop: 10,
        }}
      >
        To
      </label>
      <ReactDatetime
        value={toDisplay(to)}
        onChange={(val) => setTo(toStored(val))}
        dateFormat="MM/DD/YYYY"
        timeFormat={false}
        closeOnSelect
        inputProps={{ placeholder: 'MM/DD/YYYY', style: dateInput }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          onClick={() => {
            setFrom('');
            setTo('');
          }}
          style={footerBtnGhost}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => {
            onApply({ from, to });
            onClose();
          }}
          style={footerBtnPrimary}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Boolean popover — Yes / No
// ============================================================
function BoolPopover({
  value,
  onApply,
  onClose,
}: {
  value: boolean | null;
  onApply: (v: boolean) => void;
  onClose: () => void;
}) {
  const ref = useOutside(true, onClose);
  return (
    <div ref={ref} style={{ ...popoverBaseStyle, width: 160, padding: 4 }}>
      {[
        { v: true, l: 'Yes' },
        { v: false, l: 'No' },
      ].map((o) => {
        const on = value === o.v;
        return (
          <button
            key={o.l}
            type="button"
            onClick={() => {
              onApply(o.v);
              onClose();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              textAlign: 'left',
              padding: '8px 10px',
              border: 'none',
              background: on ? C.lineSoft : 'transparent',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 13,
              color: C.ink,
              fontWeight: 600,
            }}
            onMouseEnter={(e) => {
              if (!on) e.currentTarget.style.background = C.lineSoft;
            }}
            onMouseLeave={(e) => {
              if (!on) e.currentTarget.style.background = 'transparent';
            }}
          >
            <span style={{ flex: 1 }}>{o.l}</span>
            {on && <i className="fas fa-check" style={{ color: C.purple, fontSize: 12 }} />}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// Renders the correct popover for a field type
// ============================================================
function FieldPopover({
  field,
  value,
  options,
  onApply,
  onClose,
}: {
  field: FilterFieldDef;
  value: FilterValue | null;
  options: EnumOption[];
  onApply: (v: FilterValue) => void;
  onClose: () => void;
}) {
  if (field.type === 'enum') {
    return (
      <EnumPopover
        field={field}
        options={options}
        selected={(value as string[]) || []}
        onApply={(vals) => onApply(vals)}
        onClose={onClose}
      />
    );
  }
  if (field.type === 'text') {
    return <TextPopover field={field} value={(value as string) || ''} onApply={(v) => onApply(v)} onClose={onClose} />;
  }
  if (field.type === 'number') {
    return (
      <NumberPopover
        field={field}
        value={(value as NumberFilterValue) || null}
        onApply={(v) => onApply(v)}
        onClose={onClose}
      />
    );
  }
  if (field.type === 'daterange') {
    return (
      <DatePopover
        value={(value as { from: string; to: string }) || null}
        onApply={(v) => onApply(v)}
        onClose={onClose}
      />
    );
  }
  return <BoolPopover value={(value as boolean) ?? null} onApply={(v) => onApply(v)} onClose={onClose} />;
}

// value is "empty" (=> should not create / should remove a chip)
function isEmptyValue(field: FilterFieldDef, value: FilterValue): boolean {
  if (field.type === 'enum') return !(value as string[])?.length;
  if (field.type === 'text') return !((value as string) || '').trim();
  if (field.type === 'number') {
    const v = value as NumberFilterValue;
    return !v || v.value === null || v.value === undefined || Number.isNaN(v.value);
  }
  if (field.type === 'daterange') {
    const v = value as { from: string; to: string };
    return !v?.from && !v?.to;
  }
  return value === null || value === undefined; // boolean: false is a valid value
}

// ============================================================
// "+ Filter" button + field-picker menu
// ============================================================
function AddFilter({
  fields,
  activeKeys,
  optionsFor,
  onAdd,
}: {
  fields: FilterFieldDef[];
  activeKeys: string[];
  optionsFor: (key: string) => EnumOption[];
  onAdd: (key: string, value: FilterValue) => void;
}) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState<FilterFieldDef | null>(null);
  const ref = useOutside(open, () => {
    setOpen(false);
    setPicking(null);
  });

  const close = () => {
    setOpen(false);
    setPicking(null);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '9px 14px',
          borderRadius: 9,
          border: `1px dashed ${open ? C.purple : '#cbd2dc'}`,
          background: open ? C.purpleSoft : '#fff',
          color: open ? C.purple : C.inkSoft,
          fontSize: 13.5,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          transition: 'all .15s',
        }}
        onMouseEnter={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = C.purple;
            e.currentTarget.style.color = C.purple;
          }
        }}
        onMouseLeave={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = '#cbd2dc';
            e.currentTarget.style.color = C.inkSoft;
          }
        }}
      >
        <i className="fas fa-filter" style={{ fontSize: 12 }} /> Filter
      </button>

      {open && !picking && (
        <div role="menu" style={{ ...popoverBaseStyle, width: 220, padding: 4 }}>
          <div
            style={{
              padding: '6px 10px 4px',
              fontSize: 10.5,
              fontWeight: 800,
              color: C.inkMute,
              textTransform: 'uppercase',
              letterSpacing: '.06em',
            }}
          >
            Filter by
          </div>
          {fields.map((f) => {
            const isActive = activeKeys.includes(f.key);
            return (
              <button
                key={f.key}
                type="button"
                role="menuitem"
                onClick={() => setPicking(f)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  background: 'transparent',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  color: C.ink,
                  fontWeight: 600,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.lineSoft)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <i
                  className={`fas ${f.icon}`}
                  style={{ color: C.inkMute, fontSize: 13, width: 16, textAlign: 'center' }}
                />
                <span style={{ flex: 1 }}>{f.label}</span>
                {isActive && <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.purple }} />}
              </button>
            );
          })}
        </div>
      )}

      {open && picking && (
        <FieldPopover
          field={picking}
          value={null}
          options={optionsFor(picking.key)}
          onApply={(v) => {
            if (!isEmptyValue(picking, v)) onAdd(picking.key, v);
            close();
          }}
          onClose={close}
        />
      )}
    </div>
  );
}

// ============================================================
// Active filter chip (editable + removable)
// ============================================================
function Chip({
  field,
  value,
  options,
  onChange,
  onRemove,
}: {
  field: FilterFieldDef;
  value: FilterValue;
  options: EnumOption[];
  onChange: (v: FilterValue) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));

  let valueText = '';
  if (field.type === 'enum') {
    const vals = value as string[];
    if (vals.length === 1) {
      valueText = options.find((o) => o.value === vals[0])?.label || vals[0];
    } else {
      valueText = `${vals.length} selected`;
    }
  } else if (field.type === 'text') {
    valueText = value as string;
  } else if (field.type === 'number') {
    const v = value as NumberFilterValue;
    valueText = `${numberOpSymbol(v.op)} ${v.value}`;
  } else if (field.type === 'daterange') {
    const v = value as { from: string; to: string };
    valueText = `${fmtDate(v.from)} – ${fmtDate(v.to)}`;
  } else {
    valueText = (value as boolean) ? 'Yes' : 'No';
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'stretch',
          border: `1px solid ${open ? C.purple : '#d7dce3'}`,
          borderRadius: 9,
          overflow: 'hidden',
          background: '#fff',
          boxShadow: open ? `0 0 0 3px ${C.purpleSoft}` : 'none',
          transition: 'box-shadow .15s, border-color .15s',
        }}
      >
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            padding: '6px 4px 6px 11px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        >
          <i className={`fas ${field.icon}`} style={{ color: C.inkMute, fontSize: 12 }} />
          <span style={{ color: C.inkMute, fontWeight: 600 }}>{field.label}</span>
          <span style={{ color: '#cbd2dc' }}>·</span>
          <span style={{ color: C.ink, fontWeight: 700 }}>{valueText}</span>
          <i
            className="fas fa-chevron-down"
            style={{
              color: C.inkMute,
              fontSize: 10,
              marginLeft: 1,
              transform: `rotate(${open ? 180 : 0}deg)`,
              transition: 'transform .15s',
            }}
          />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${field.label} filter`}
          title="Remove filter"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 8px',
            border: 'none',
            borderLeft: `1px solid ${C.lineSoft}`,
            background: 'transparent',
            color: C.inkMute,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#fdeef1';
            e.currentTarget.style.color = C.red;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = C.inkMute;
          }}
        >
          <i className="fas fa-times" style={{ fontSize: 12 }} />
        </button>
      </div>

      {open && (
        <FieldPopover
          field={field}
          value={value}
          options={options}
          onApply={(v) => {
            if (isEmptyValue(field, v)) onRemove();
            else onChange(v);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// FilterBar — orchestrator (button + active chips row)
// ============================================================
export interface FilterBarProps {
  fields: FilterFieldDef[];
  filters: ActiveFilter[];
  onChange: (filters: ActiveFilter[]) => void;
  optionsFor: (key: string) => EnumOption[];
  /** Extra content rendered to the left of the "+ Filter" button (e.g. Create New). */
  trailing?: React.ReactNode;
}

export function FilterBar({ fields, filters, onChange, optionsFor, trailing }: FilterBarProps) {
  const activeKeys = filters.map((f) => f.key);

  const addOrReplace = (key: string, value: FilterValue) => {
    const exists = filters.some((f) => f.key === key);
    onChange(exists ? filters.map((f) => (f.key === key ? { key, value } : f)) : [...filters, { key, value }]);
  };
  const changeFilter = (key: string, value: FilterValue) =>
    onChange(filters.map((f) => (f.key === key ? { key, value } : f)));
  const removeFilter = (key: string) => onChange(filters.filter((f) => f.key !== key));
  const clearAll = () => onChange([]);

  const fieldByKey = (key: string) => fields.find((f) => f.key === key);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <AddFilter fields={fields} activeKeys={activeKeys} optionsFor={optionsFor} onAdd={addOrReplace} />
        {trailing}
      </div>

      {filters.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginTop: 14,
          }}
        >
          {filters.map((f) => {
            const field = fieldByKey(f.key);
            if (!field) return null;
            return (
              <Chip
                key={f.key}
                field={field}
                value={f.value}
                options={optionsFor(f.key)}
                onChange={(v) => changeFilter(f.key, v)}
                onRemove={() => removeFilter(f.key)}
              />
            );
          })}
          <button
            type="button"
            onClick={clearAll}
            style={{
              padding: '6px 12px',
              border: 'none',
              background: 'transparent',
              color: C.inkMute,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              borderRadius: 7,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.lineSoft;
              e.currentTarget.style.color = C.red;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = C.inkMute;
            }}
          >
            Clear all
          </button>
        </div>
      )}
    </>
  );
}

export default FilterBar;
